import React, { useRef } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { Form } from '../../../../components';
import { FieldSearchableSelectComponent } from '../../../../components/FieldSearchableSelect/FieldSearchableSelect';

import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button
        className={css.searchSubmit}
        aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
      >
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.placeholder',
              })}
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};
const SubmitButton = props => {
  const intl = useIntl();
  return (
    <button
      className={css.searchSubmit}
      aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
      type="submit"
      {...props}
    >
      <IconSearchDesktop />
    </button>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        // Merge the standard onChange function with custom behaviur. A better solution would
        // be to use the FormSpy component from Final Form and pass onChange to the
        // onChange prop but that breaks due to insufficient subscription handling.
        // See: https://github.com/final-form/react-final-form/issues/159
        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            id={isMobile ? 'location-search-mobile' : 'location-search'}
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
            submitButton={SubmitButton}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
          />
        );
      }}
    />
  );
};

/**
 * The main search form for the Topbar.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.desktopInputRoot root class for desktop form input
 * @param {Function} props.onSubmit
 * @param {boolean} props.isMobile
 * @param {Object} props.appConfig
 * @returns {JSX.Element} search form element
 */
const TopbarSearchForm = props => {
  const searchInputRef = useRef(null);
  const intl = useIntl();
  const config = useConfiguration();
  const { appConfig, onSubmit, ...restOfProps } = props;

  // School options: use listing field enumOptions when present (Select one), otherwise
  // config.listing.schoolOptions (Free text field workaround for 100+ options).
  const listingFields = config?.listing?.listingFields || [];
  const schoolField = listingFields.find(f => {
    const fieldKeyLower = (f.key || '').toLowerCase();
    return fieldKeyLower === 'school' || fieldKeyLower === 'schoolname';
  });
  const schoolOptions =
    schoolField?.enumOptions?.length > 0
      ? schoolField.enumOptions
      : config?.listing?.schoolOptions || [];

  const onSchoolChange = (schoolValue) => {
    if (schoolValue) {
      // Auto-submit when a school is selected
      onSubmit({ pub_SchoolName: schoolValue });
      searchInputRef?.current?.blur();
    }
  };

  const onSchoolSubmit = values => {
    if (values?.schoolSearch) {
      // Navigate to search page with school filter
      onSubmit({ pub_SchoolName: values.schoolSearch });
      searchInputRef?.current?.blur();
    }
  };

  const submit = onSchoolSubmit;
  return (
    <FinalForm
      {...restOfProps}
      onSubmit={submit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          isMobile = false,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const searchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobile ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            <div className={searchWrapperClasses}>
              <button
                type="submit"
                className={css.searchSubmit}
                aria-label={intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' })}
              >
                <div className={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}>
                  <IconSearchDesktop />
                </div>
              </button>
              <Field
                name="schoolSearch"
                render={({ input }) => {
                  const { onChange, ...restInput } = input;
                  
                  // Merge the standard onChange function with auto-submit behavior
                  const handleSchoolChange = value => {
                    onChange(value);
                    onSchoolChange(value);
                  };

                  return (
                    <FieldSearchableSelectComponent
                      input={{ ...restInput, onChange: handleSchoolChange }}
                      name="schoolSearch"
                      id={isMobile ? 'school-search-mobile' : 'school-search'}
                      options={schoolOptions}
                      placeholder="Search for your school"
                      inputClassName={isMobile ? css.mobileInput : css.desktopInput}
                      inputRef={searchInputRef}
                      showRightIcon={false}
                    />
                  );
                }}
              />
            </div>
          </Form>
        );
      }}
    />
  );
};

export default TopbarSearchForm;
